import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye, FaTrash, FaSearch, FaUserGraduate } from 'react-icons/fa';
import { registerAPI } from '../services/api';

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    useEffect(() => {
        fetchUsers();
    }, [currentPage]);
    
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await registerAPI.getAllUsers(currentPage, 10);
            if (response.data.success) {
                setUsers(response.data.data);
                setTotalPages(response.data.pagination.totalPages);
            }
        } catch (error) {
            toast.error('Error fetching users');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            fetchUsers();
            return;
        }
        
        setLoading(true);
        try {
            const response = await registerAPI.searchUsers(searchTerm);
            if (response.data.success) {
                setUsers(response.data.data);
                setTotalPages(1);
            }
        } catch (error) {
            toast.error('Error searching users');
        } finally {
            setLoading(false);
        }
    };
    
    const handleDelete = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await registerAPI.deleteUser(userId);
                toast.success('User deleted successfully');
                fetchUsers();
            } catch (error) {
                toast.error('Error deleting user');
            }
        }
    };
    
    return (
        <div className="min-h-screen py-8 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="card">
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                                <FaUserGraduate className="mr-2 text-indigo-600" />
                                Registered Users
                            </h2>
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    placeholder="Search by name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="form-input w-64"
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <button
                                    onClick={handleSearch}
                                    className="btn-primary"
                                >
                                    <FaSearch />
                                </button>
                            </div>
                        </div>
                        
                        {loading ? (
                            <div className="text-center py-8">
                                <div className="inline-block w-8 h-8 border-t-2 border-indigo-600 rounded-full animate-spin"></div>
                                <p className="mt-2 text-gray-600">Loading...</p>
                            </div>
                        ) : users.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                No users found
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gender</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DOB</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marital Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {users.map((user) => (
                                                <tr key={user.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.id}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            {user.image && (
                                                                <img 
                                                                    src={`https://app.bihebari.com.np/uploads/${user.image}`} 
                                                                    alt={user.name}
                                                                    className="w-8 h-8 rounded-full object-cover mr-2"
                                                                />
                                                            )}
                                                            <span className="text-sm font-medium text-gray-900">{user.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.gender}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.dob}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.marital}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                                            user.pay_status === 'paid' ? 'bg-green-100 text-green-800' :
                                                            user.pay_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-red-100 text-red-800'
                                                        }`}>
                                                            {user.pay_status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <Link
                                                                to={`/user/${user.id}`}
                                                                className="text-indigo-600 hover:text-indigo-900"
                                                            >
                                                                <FaEye />
                                                            </Link>
                                                            <button
                                                                onClick={() => handleDelete(user.id)}
                                                                className="text-red-600 hover:text-red-900"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex justify-center space-x-2 mt-6">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1 border rounded disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        <span className="px-3 py-1">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1 border rounded disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserList;